import Numeral from 'numeral';
import 'numeral/locales/no';
import React, { useState } from 'react';
import { range, union } from 'underscore';

import { StandardButton } from '@eika/button';
import { Checkbox } from '@eika/checkbox';
import { Input } from '@eika/input';
import { ErrorLabel } from '@eika/label';

import { ButtonClickEvent, InputChangeEvent, NameValueString, StringKeyValue } from '../../../actions/types';
import { ApplicationReducer } from '../../../reducers/application';
import { CardsReducer } from '../../../reducers/cards';
import { ConsentReducer } from '../../../reducers/consent';
import ValidationFields from '../../../util/validation/ValidationFields';
import { isValidPin } from '../../../util/validation/validators';
import StepSelect from '../../Common/StepSelect';
import Consent from './Consent';
import TrafficLight from './TrafficLight';

import ApprovedIcon from '../../../assets/img/ApprovedIcon.svg';

interface Props {
    application: ApplicationReducer;
    consent: ConsentReducer;
    text: StringKeyValue;
    cards: CardsReducer;
    showSubmitButton: boolean;
    action: any;
}

const SummaryComponent = ({ text, cards, application, consent, action, showSubmitButton }: Props): JSX.Element => {
    Numeral.locale('no');
    const [customPin, setCustomPin] = useState(false);
    const [pin, setPin] = useState('');
    const [pinConfirm, setPinConfirm] = useState('');

    const { cardNameGold, cardNameBusiness, summarySetPinEnabled } = text;
    const { korttype, kortId, supportedCards } = cards;
    const { kredittgrense, trafficLight, errors } = application;

    let currentCard = cards.goldCard;
    if (korttype === supportedCards.business) {
        currentCard = cards.businessCard;
    }

    const lowCredit = 30000;
    const mediumCredit = 200000;

    let stepper = 5000;
    let addon = 2500;

    if (currentCard.kredittgrense > lowCredit) {
        stepper = 10000;
        addon = 5000;
    } else if (currentCard.kredittgrense > mediumCredit) {
        stepper = 50000;
        addon = 10000;
    }

    /* Round to closest stepper amount */
    const newCreditLimit = Math.round(Math.ceil((currentCard.kredittgrense + addon) / stepper) * stepper) || stepper;

    const fullRange = union(range(5000, 30000, 5000), range(30000, 200000, 10000), range(200000, 1050000, 50000));
    let creditRange = fullRange
        .filter((value) => value >= newCreditLimit && value <= kredittgrense)
        .map((value) => ({
            text: Numeral(value).format('0,0'),
            value,
        }));

    if (creditRange.length < 1) {
        creditRange = [{ text: Numeral(newCreditLimit).format('0,0'), value: newCreditLimit }];
    }

    /* No amount selected, set default */
    const creditRangeEndIndex = creditRange.length - 1;
    const creditRangeEndValue = creditRange[creditRangeEndIndex]?.value || 0;
    if (kredittgrense && !application.kredittbelop) {
        application.kredittbelop = creditRangeEndValue;
    }

    let cardLabel;
    if (korttype === supportedCards.gold) {
        cardLabel = cardNameGold;
    } else if (korttype === supportedCards.business) {
        cardLabel = cardNameBusiness;
    }

    /*     const onChangeButtonGroup = (name: string, value: string | number) => {
        action.changeSoknadField(name, String(value));
    }; */

    const onChangeCreditButtonGroup = (e: NameValueString) => {
        action.changeSoknadField(e.name, e.value);
        action.verifySoknadField(e.name, e.value);
    };

    const onBlur = (e: any) => {
        action.changeSoknadField(e.target.name, e.target.value);
        action.verifySoknadField(e.target.name, e.target.value);
    };

    const validatePinChange = (e: InputChangeEvent) => {
        const { name, value } = e.target;
        if (value.length === 4) {
            action.changeSoknadField(name, value);
            action.verifySoknadField(name, value);
        }
    };

    const onPinChange = (e: any) => {
        validatePinChange(e);
        const { value } = e.target;
        if (value.length < 5 && !Number.isNaN(Number(value))) {
            setPin(value);
        }
    };

    const onPinChangeConfirm = (e: any) => {
        validatePinChange(e);
        const { value } = e.target;
        if (value.length < 5 && !Number.isNaN(Number(value))) {
            setPinConfirm(value);
        }
    };

    const onChangeCheckbox = (/* e: InputChangeEvent */) => {
        if (customPin) action.verifySoknadField(ValidationFields.CUSTOM_PIN_KODE, '');
        setCustomPin(!customPin);
        setPin('');
        setPinConfirm('');
    };

    let forfallsdato;

    const newCard = kortId === '';

    /* if (newCard) {
        forfallsdato = (
            <div className="credit-card-application-form-group">
                <ButtonToggles
                    label={text.summaryDesiredDueDateTitle}
                    onChange={(e) => onChangeButtonGroup(ValidationFields.FORFALLSDATO, e)}
                    selectedOption={application.forfallsdato}
                    options={[
                        { text: '5', value: '1' },
                        { text: '15', value: '2' },
                        { text: '25', value: '3' },
                    ].map((val: any) => {
                        return { label: val.text, value: val.value };
                    })}
                />
            </div>
        );
    } */

    const onSubmit = (e: ButtonClickEvent) => {
        e.preventDefault();
        if (customPin) {
            if (!isValidPin(pin) || pin !== pinConfirm) {
                action.verifySoknadField(ValidationFields.PIN_KODE, pin);
                action.verifySoknadField(ValidationFields.PIN_KODE_CONFIRM, pinConfirm);
                action.verifyBestillingSkjema();
            } else {
                action.summaryNextEvent();
            }
        } else {
            action.summaryNextEvent();
        }
    };

    return (
        <div>
            <div className="text-center page-header">
                <img src={ApprovedIcon} alt="" />
                <h1>{text.summaryInfoTitle}</h1>
            </div>
            <div className="summary__info">
                {text.summaryInfoTextPre} {Numeral(kredittgrense).format('0,0')} kroner {text.summaryInfoTextPost}
            </div>
            <div className="credit-card-application-form-group">
                {text.summarySelectCreditTitle && (
                    <div className="credit-card-application-form-group__label">{text.summarySelectCreditTitle}</div>
                )}

                <div id="summaryLoanAmount">
                    <StepSelect
                        name={ValidationFields.KREDITTBELOP}
                        label={cardLabel}
                        values={creditRange}
                        defaultIndex={creditRangeEndIndex}
                        onChange={onChangeCreditButtonGroup}
                    />
                </div>

                {errors.kredittbelop && <ErrorLabel text={errors.kredittbelop} />}
            </div>
            {trafficLight && <TrafficLight trafficLight={trafficLight} text={text} />}

            {forfallsdato}

            {summarySetPinEnabled === 'true' && newCard && (
                <div className="credit-card-application-form-group">
                    <div className="credit-card-application-form-group__label">{text.summarySetPinTitle}</div>
                    <Checkbox label={text.summarySetPinCheckboxLabel} checked={customPin} onChange={onChangeCheckbox} />
                    {customPin && (
                        <div>
                            <div className="summary__pin">
                                <Input
                                    name={ValidationFields.PIN_KODE}
                                    value={pin}
                                    error={errors.pinkode}
                                    onChange={onPinChange}
                                    onBlur={onBlur}
                                    autoComplete="off"
                                    label={text.summarySetPinLabel}
                                />
                            </div>

                            {pin.length === 4 && !errors.pinkode && (
                                <div className="summary__pin">
                                    <Input
                                        name={ValidationFields.PIN_KODE_CONFIRM}
                                        value={pinConfirm}
                                        error={errors.pinConfirm}
                                        onChange={onPinChangeConfirm}
                                        onBlur={onBlur}
                                        autoComplete="off"
                                        label={text.summarySetPinLabelConfirm}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {!!consent?.data?.length && (
                <div className="summary__consent">
                    {consent.data.map((consent) => (
                        <Consent
                            data={consent}
                            onChange={action.updateConsent}
                            text={text}
                            key={consent.samtykkeId.value}
                        />
                    ))}
                </div>
            )}

            {showSubmitButton && (
                <div className="summary__submit">
                    <StandardButton onClick={onSubmit}>{text.summarySendBestillingButton}</StandardButton>
                </div>
            )}

            {errors.formError && (
                <div className="text-center" style={{ marginTop: '40px' }}>
                    <ErrorLabel text={errors.formError} />
                </div>
            )}
        </div>
    );
};

export default SummaryComponent;
